import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Sandbox() {
  const [operators, setOperators] = useState([]);
  const [players, setPlayers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [games, setGames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // form
  const [form, setForm] = useState({
    operator_id: "", player_id: "", provider_id: "", game_id: "",
    initial_balance: "1000", amount: "10",
  });

  const load = async () => {
    const [op, pl, pr, gm] = await Promise.all([
      api.get("/api/admin/operators"),
      api.get("/api/admin/sandbox/players"),
      api.get("/api/admin/providers"),
      api.get("/api/admin/games?limit=200"),
    ]);
    setOperators(op.data); setPlayers(pl.data); setProviders(pr.data); setGames(gm.data);
    if (op.data[0]) setForm(f => ({ ...f, operator_id: f.operator_id || op.data[0].operator_id }));
    if (pr.data[0]) setForm(f => ({ ...f, provider_id: f.provider_id || pr.data[0].provider_id }));
  };
  useEffect(() => { load(); }, []);

  const call = async (path, body) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(path, body);
      setResult({ path, body, response: data });
      toast.success("Simulation complete");
      load();
    } catch (e) {
      setResult({ path, body, error: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const createPlayer = () => call("/api/admin/sandbox/player/create", {
    operator_id: form.operator_id, initial_balance: parseFloat(form.initial_balance), currency: "USD",
  });
  const simulateBet = () => call("/api/admin/sandbox/simulate/bet", {
    operator_id: form.operator_id, player_id: form.player_id, amount: parseFloat(form.amount),
    provider_id: form.provider_id, game_id: form.game_id, round_id: `rnd_${Date.now()}`,
  });
  const simulateWin = () => call("/api/admin/sandbox/simulate/win", {
    operator_id: form.operator_id, player_id: form.player_id, amount: parseFloat(form.amount) * 2,
    provider_id: form.provider_id, game_id: form.game_id,
  });
  const simulateDup = () => call("/api/admin/sandbox/simulate/duplicate-callback", {
    operator_id: form.operator_id, player_id: form.player_id, amount: parseFloat(form.amount),
  });
  const simulateInsuff = () => call("/api/admin/sandbox/simulate/insufficient-funds", {
    operator_id: form.operator_id, player_id: form.player_id, amount: 9999999,
  });
  const simulateTimeout = () => call("/api/admin/sandbox/simulate/timeout", {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="sandbox-page">
      {/* Left: form */}
      <div className="space-y-5">
        <div className="card-surface p-5">
          <div className="text-sm font-semibold mb-4">1. Pick context</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Operator">
              <select value={form.operator_id} onChange={(e) => setForm({ ...form, operator_id: e.target.value })}
                      className="w-full px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--border)] rounded text-xs" data-testid="sandbox-operator">
                {operators.map(o => <option key={o.operator_id} value={o.operator_id}>{o.operator_name}</option>)}
              </select>
            </Field>
            <Field label="Provider">
              <select value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}
                      className="w-full px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--border)] rounded text-xs" data-testid="sandbox-provider">
                {providers.map(p => <option key={p.provider_id} value={p.provider_id}>{p.provider_name}</option>)}
              </select>
            </Field>
            <Field label="Game">
              <select value={form.game_id} onChange={(e) => setForm({ ...form, game_id: e.target.value })}
                      className="w-full px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--border)] rounded text-xs" data-testid="sandbox-game">
                <option value="">— pick game —</option>
                {games.filter(g => !form.provider_id || g.provider_id === form.provider_id).slice(0, 50).map(g => <option key={g.game_id} value={g.game_id}>{g.game_name}</option>)}
              </select>
            </Field>
            <Field label="Player">
              <select value={form.player_id} onChange={(e) => setForm({ ...form, player_id: e.target.value })}
                      className="w-full px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--border)] rounded text-xs" data-testid="sandbox-player">
                <option value="">— pick player —</option>
                {players.filter(p => !form.operator_id || p.operator_id === form.operator_id).map(p => <option key={p.player_id} value={p.player_id}>{p.player_id}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <div className="card-surface p-5">
          <div className="text-sm font-semibold mb-4">2. Create sandbox player</div>
          <div className="flex gap-3 items-end">
            <Field label="Initial balance" className="flex-1">
              <input value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
                     className="w-full px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--border)] rounded text-xs font-mono" data-testid="sandbox-initial-balance"/>
            </Field>
            <button onClick={createPlayer} disabled={busy || !form.operator_id} className="btn-gold px-4 py-2 rounded text-xs flex items-center gap-1" data-testid="sandbox-create-player">
              <Play size={12}/> Create
            </button>
          </div>
        </div>

        <div className="card-surface p-5">
          <div className="text-sm font-semibold mb-4">3. Simulate flows</div>
          <Field label="Amount">
            <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   className="w-full px-2 py-1.5 bg-[var(--surface-2)] border border-[var(--border)] rounded text-xs font-mono" data-testid="sandbox-amount"/>
          </Field>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button onClick={simulateBet} disabled={busy || !form.player_id} className="btn-ghost py-2 rounded text-xs" data-testid="sim-bet">Simulate BET</button>
            <button onClick={simulateWin} disabled={busy || !form.player_id} className="btn-ghost py-2 rounded text-xs" data-testid="sim-win">Simulate WIN</button>
            <button onClick={simulateDup} disabled={busy || !form.player_id} className="btn-ghost py-2 rounded text-xs" data-testid="sim-dup">Duplicate callback</button>
            <button onClick={simulateInsuff} disabled={busy || !form.player_id} className="btn-ghost py-2 rounded text-xs" data-testid="sim-insuff">Insufficient funds</button>
            <button onClick={simulateTimeout} disabled={busy} className="btn-ghost py-2 rounded text-xs col-span-2" data-testid="sim-timeout">Provider timeout</button>
          </div>
        </div>
      </div>

      {/* Right: result */}
      <div className="card-surface p-5 sticky top-24 h-fit" data-testid="sandbox-result">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">Live result</div>
          {busy && <Loader2 className="animate-spin" size={14}/>}
        </div>
        {!result && <div className="text-[var(--text-3)] text-sm">Run a simulation to see the request, response, ledger entry, and webhook result.</div>}
        {result?.error && <div className="text-[var(--error)] text-sm">{result.error}</div>}
        {result?.response && (
          <div className="space-y-4">
            <Section title="Request" data={result.body}/>
            <Section title="Response" data={result.response}/>
            {result.response.ledger && <Section title="Ledger entry" data={result.response.ledger}/>}
            {result.response.webhook && (
              <Section title="Webhook delivery" data={result.response.webhook}/>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function Section({ title, data }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)] mb-2">{title}</div>
      <pre className="font-mono text-xs bg-[var(--bg)] border border-[var(--border)] rounded p-3 overflow-x-auto max-h-60">
{JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
